import { useEffect, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { AuditHistory } from "@/components/audit/AuditHistory";
import { api } from "@/lib/apiClient";
import type { ArticleCategory } from "@/types/camping";

export interface EditArticleCategoryModalProps {
  open: boolean;
  category: ArticleCategory | null;
  onClose: () => void;
  onSaved: () => void;
  canEdit: boolean;
}

export function EditArticleCategoryModal({
  open,
  category,
  onClose,
  onSaved,
  canEdit,
}: EditArticleCategoryModalProps) {
  const [form, setForm] = useState({
    name: "",
    article_number_pattern: "",
    sort_order: "0",
    for_quick_sale: false,
    is_active: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !category) return;
    setForm({
      name: category.name,
      article_number_pattern: category.article_number_pattern,
      sort_order: String(category.sort_order),
      for_quick_sale: category.for_quick_sale,
      is_active: category.is_active,
    });
    setError(null);
    setSubmitting(false);
  }, [open, category]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!category || !canEdit) return;
    setError(null);
    setSubmitting(true);
    try {
      await api.patch(`/api/v1/article-categories/${category.id}`, {
        name: form.name.trim(),
        article_number_pattern: form.article_number_pattern.trim(),
        sort_order: Number(form.sort_order) || 0,
        for_quick_sale: form.for_quick_sale,
        is_active: form.is_active,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Speichern");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={category?.name ?? "Artikelkategorie"}
      description={canEdit ? "Kategorie bearbeiten" : "Nur Lesen"}
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <label className="flex flex-col gap-1 text-sm">
          Name
          <input
            required
            disabled={!canEdit}
            className="eh-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Nummernschema
          <input
            required
            disabled={!canEdit}
            className="eh-input font-mono"
            value={form.article_number_pattern}
            onChange={(e) =>
              setForm((f) => ({ ...f, article_number_pattern: e.target.value }))
            }
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          Sortierung
          <input
            type="number"
            disabled={!canEdit}
            className="eh-input w-32"
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={form.for_quick_sale}
            onChange={(e) => setForm((f) => ({ ...f, for_quick_sale: e.target.checked }))}
          />
          Für Schnellverkauf
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canEdit}
            checked={form.is_active}
            onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
          />
          Aktiv
        </label>
        {category ? (
          <AuditHistory entityType="article_category" entityId={category.id} active={open} />
        ) : null}
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <div className="eh-modal-actions flex flex-wrap justify-end gap-2 border-t border-separator pt-4">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Schließen
          </Button>
          {canEdit ? (
            <Button type="submit" loading={submitting}>
              Speichern
            </Button>
          ) : null}
        </div>
      </form>
    </Modal>
  );
}
